import {
  Clock,
  Globe,
  Map,
  Network,
  Package,
  Repeat,
  ShieldCheck,
  Timer,
  Flag,
  Milestone,
  CheckCheck,
  X,
  Loader2,
} from 'lucide-react';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useLanguage } from '@/context/LanguageContext';
import {
  DiagnosticsService,
  DSCP_POST_VALUE,
  type DiagnosticParamPost,
} from '@/services/diagnosticsService';
import {
  DiagnosticAbortError,
  DiagnosticTimeoutError,
  DiagnosticTriggerError,
  isCompleteStatus,
  type TracerouteSseResult,
} from '@/services/diagnosticsSse';
import { useDiagnosticRunner } from '@/hooks/useDiagnosticRunner';

import type {
  HopType,
  TracertRef,
  TracertProps,
  TracertStats,
  TimeMetricItem,
  ConfigItemData,
  StatusInfo,
} from './types';
import { INITIAL_STATS } from './types';
import {
  parseHopResults,
  fetchHopLocations,
  findParam,
  isTracerouteInput,
  getLatencyThresholdColor,
} from './utils';

import {
  MainContainer,
  ToolContentBox,
  LoadingOverlay,
  LoadingText,
  CardsContainer,
  TwoCardsRow,
} from './styles';

import {
  TracertHeader,
  ResultsCard,
  ConfigCard,
  HopsDiagramCard,
  MapCard,
  EmptyStateCard,
} from './components';

export type { HopType, TracertRef, TracertProps };

const Tracert = forwardRef<TracertRef, TracertProps>(
  ({ onStateChange, equipmentId }, ref) => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const theme = useTheme();

    const [traceStarted, setTraceStarted] = useState<boolean>(false);
    const [isDone, setIsDone] = useState<boolean>(false);
    const [stats, setStats] = useState<TracertStats>(INITIAL_STATS);
    const [route, setRoute] = useState<HopType[]>([]);
    const [expanded, setExpanded] = useState<boolean>(false);
    const [mapped, setMapped] = useState<boolean>(false);
    const [mapping, setMapping] = useState<boolean>(false);
    const { run, cancel } = useDiagnosticRunner();

    const isSm = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
      if (isSm) {
        setExpanded(true);
      }
    }, [isSm]);

    const handleMap = async () => {
      setMapping(true);
      try {
        if (!mapped) {
          const enriched = await fetchHopLocations(route, language);
          setRoute(enriched);
        }
        setMapped((prev) => !prev);
      } finally {
        setMapping(false);
      }
    };

    const startTrace = useCallback(
      async (
        ipToTest: string,
        customMaxHops?: string | number,
        customTimeout?: string | number
      ) => {
        if (!equipmentId) {
          enqueueSnackbar(t('tools.errors.invalidEquipmentOrInterface'), {
            variant: 'error',
          });
          return;
        }

        setTraceStarted(true);
        setIsDone(false);
        setMapped(false);
        setRoute([]);
        setStats({
          ...INITIAL_STATS,
          state: 'Requested',
          host: ipToTest,
          maxHopCount: customMaxHops ? String(customMaxHops) : 'N/A',
          timeout: customTimeout ? String(customTimeout) : 'N/A',
        });

        try {
          const { params, stateParamId, dscpParamId } =
            await DiagnosticsService.getParams(equipmentId, 'traceroute');

          const stateParamEntry: DiagnosticParamPost | null = stateParamId
            ? { id: stateParamId, value: 'Requested' }
            : null;

          let body: DiagnosticParamPost[];
          if (params.length > 0) {
            const hostParam = findParam(params, 'host', 'Host');
            const maxHopParam = findParam(params, 'maxHopCount', 'MaxHopCount');
            const timeoutParam = findParam(params, 'timeout', 'Timeout');
            const inputParams = params.filter(isTracerouteInput).map(
              (p): DiagnosticParamPost => ({
                id: p.id,
                value:
                  p.id === dscpParamId
                    ? DSCP_POST_VALUE
                    : p.id === hostParam?.id
                      ? ipToTest
                      : p.id === maxHopParam?.id && customMaxHops
                        ? String(customMaxHops)
                        : p.id === timeoutParam?.id && customTimeout
                          ? String(customTimeout)
                          : (p.value ?? ''),
              })
            );
            body = [
              ...(stateParamEntry ? [stateParamEntry] : []),
              ...inputParams,
            ];
          } else {
            body = [
              ...(stateParamEntry ? [stateParamEntry] : []),
              { id: 0, value: ipToTest },
            ];
          }

          // Fields the UI shows that the SSE payload no longer carries — fall
          // back to the values we requested.
          const requestedTries =
            findParam(params, 'NumberOfTries')?.value ?? 'N/A';
          const requestedBlockSize =
            findParam(params, 'DataBlockSize')?.value ?? 'N/A';

          // Opens the SSE stream, POSTs the trigger (202) and resolves with
          // the DiagnosticResult event sent before the stream closes.
          const { result } = await run<TracerouteSseResult>(
            equipmentId,
            'traceroute',
            body
          );

          const rawHops = result.result ?? [];

          setStats({
            state: isCompleteStatus(result.status)
              ? 'Complete'
              : result.responseTime
                ? 'Complete'
                : 'N/A',
            host: result.host ?? ipToTest,
            responseTime: result.responseTime ?? 'N/A',
            hopsCount: rawHops.length > 0 ? String(rawHops.length) : 'N/A',
            maxHopCount: customMaxHops ? String(customMaxHops) : 'N/A',
            numberTries: requestedTries,
            timeout:
              result.timeout ?? (customTimeout ? String(customTimeout) : 'N/A'),
            dataBlockSize: requestedBlockSize,
            dscp: result.dscp ?? 'N/A',
            interfaceTest: result.interfaceTest ?? 'N/A',
          });

          let parsedHops: HopType[] = [];
          if (rawHops.length > 0) {
            parsedHops = parseHopResults(rawHops, ipToTest);
          } else if (ipToTest) {
            parsedHops = [
              {
                hop: 1,
                address: ipToTest,
                name: ipToTest,
                latency: [
                  result.responseTime ? `${result.responseTime} ms` : '*',
                ],
              },
            ];
          }

          setRoute(parsedHops);
          if (parsedHops.length > 0) {
            fetchHopLocations(parsedHops, language).then((enriched) => {
              setRoute(enriched);
            });
          }
          setTraceStarted(false);
          setIsDone(true);
        } catch (err) {
          // A cancelled run already had its UI state restored by resetTrace.
          if (err instanceof DiagnosticAbortError) return;

          enqueueSnackbar(
            err instanceof DiagnosticTimeoutError
              ? t('tools.errors.diagnosticTimeout')
              : err instanceof DiagnosticTriggerError
                ? t('tools.errors.diagnosticTriggerFailed')
                : t('tools.errors.traceRouteFailed', {
                    error: err instanceof Error ? err.message : String(err),
                  }),
            { variant: 'error' }
          );
          setStats((prev) => ({ ...prev, state: 'Error' }));
          setTraceStarted(false);
          setIsDone(true);
        }
      },
      [equipmentId, run, t, language]
    );

    const resetTrace = useCallback(() => {
      cancel();
      setStats(INITIAL_STATS);
      setRoute([]);
      setMapped(false);
      setTraceStarted(false);
      setIsDone(false);
    }, [cancel]);

    useImperativeHandle(ref, () => ({ startTrace, resetTrace }));

    useEffect(() => {
      if (onStateChange) {
        onStateChange({ isRunning: traceStarted, isDone });
      }
    }, [traceStarted, isDone, onStateChange]);

    const isComplete =
      isDone && stats.state !== 'Error' && stats.state !== 'None';
    const isError = isDone && stats.state === 'Error';

    const statusInfo: StatusInfo = useMemo(() => {
      if (isError) {
        return {
          color: theme.palette.status.offline || '#EF4444',
          Icon: X,
          label: t('tools.error'),
        };
      }
      if (stats.state === 'Requested') {
        return {
          color:
            theme.palette.warning?.main ||
            theme.palette.status.warning ||
            '#F59E0B',
          Icon: Loader2,
          label: t('tools.traceroute.ongoing'),
        };
      }
      if (isComplete) {
        return {
          color: theme.palette.status.online || '#22C55E',
          Icon: CheckCheck,
          label: stats.state,
        };
      }
      return {
        color: theme.palette.grey['01'] || '#64748B',
        Icon: Map,
        label: stats.state || '—',
      };
    }, [isComplete, isError, stats.state, theme, t]);

    const hopsPercentage = useMemo(() => {
      const hops = Number(stats.hopsCount);
      const max = Number(stats.maxHopCount);
      if (!isNaN(hops) && !isNaN(max) && max > 0) {
        return Math.min(100, Math.max(0, (hops / max) * 100)).toFixed(1);
      }
      return null;
    }, [stats.hopsCount, stats.maxHopCount]);

    const timeMetrics: TimeMetricItem[] = useMemo(() => {
      return [
        {
          id: 'responseTime',
          icon: Clock,
          label: t('tools.traceroute.responseTime'),
          value: stats.responseTime,
          unit: 'ms',
          color: getLatencyThresholdColor(stats.responseTime, theme),
        },
        {
          id: 'hopsCount',
          icon: Milestone,
          label: t('tools.traceroute.hopsCount'),
          value: stats.hopsCount,
          unit: '',
          color: theme.palette.status.stats.min || '#00CFFF',
        },
        {
          id: 'maxHopCount',
          icon: Flag,
          label: t('tools.traceroute.maxHopCount'),
          value: stats.maxHopCount,
          unit: '',
          color: theme.palette.white['06'] || '#FFFFFF',
        },
      ];
    }, [stats, theme, t]);

    const configItems: ConfigItemData[] = useMemo(
      () => [
        {
          id: 'host',
          icon: Globe,
          label: t('tools.traceroute.host'),
          value: stats.host || '—',
        },
        {
          id: 'timeout',
          icon: Timer,
          label: t('tools.traceroute.timeout'),
          value: stats.timeout !== 'N/A' ? `${stats.timeout} ms` : '—',
        },
        {
          id: 'numberTries',
          icon: Repeat,
          label: t('tools.traceroute.numberTries'),
          value: stats.numberTries !== 'N/A' ? stats.numberTries : '—',
        },
        {
          id: 'dataBlockSize',
          icon: Package,
          label: t('tools.traceroute.dataBlockSize'),
          value:
            stats.dataBlockSize !== 'N/A' ? `${stats.dataBlockSize} B` : '—',
        },
        {
          id: 'dscp',
          icon: ShieldCheck,
          label: t('tools.traceroute.dscp'),
          value: stats.dscp !== 'N/A' ? stats.dscp : '—',
        },
        {
          id: 'interface',
          icon: Network,
          label: t('tools.traceroute.interface'),
          value: stats.interfaceTest !== 'N/A' ? stats.interfaceTest : '—',
        },
      ],
      [stats, t]
    );

    return (
      <MainContainer>
        <TracertHeader
          isDone={isDone}
          traceStarted={traceStarted}
          mapped={mapped}
          mapping={mapping}
          isComplete={isComplete}
          onToggleMap={handleMap}
        />

        <ToolContentBox>
          {/* Loading Overlay */}
          {traceStarted && (
            <LoadingOverlay>
              <CircularProgress
                enableTrackSlot
                size="20px"
                sx={{ color: theme.palette.primary.main }}
              />
              <LoadingText variant="body2">
                {t('tools.traceroute.ongoing')}
              </LoadingText>
            </LoadingOverlay>
          )}

          <CardsContainer>
            {!isDone && !traceStarted && <EmptyStateCard />}

            {/* Results section */}
            {(isDone || traceStarted) && (
              <>
                <TwoCardsRow>
                  {/* Results Card */}
                  <ResultsCard
                    host={stats.host}
                    statusInfo={statusInfo}
                    timeMetrics={timeMetrics}
                    stats={stats}
                    isComplete={isComplete}
                    isError={isError}
                    hopsPercentage={hopsPercentage}
                  />

                  {/* Config Card */}
                  {isComplete && <ConfigCard configItems={configItems} />}
                </TwoCardsRow>

                {/* Hops Diagram Card */}
                <HopsDiagramCard
                  route={route}
                  expanded={expanded}
                  isSm={isSm}
                  onToggleExpand={() => setExpanded(!expanded)}
                />

                {/* Map Card */}
                <MapCard route={route} mapped={mapped} />
              </>
            )}
          </CardsContainer>
        </ToolContentBox>
      </MainContainer>
    );
  }
);

export default Tracert;
